// Settings → Cancellation fee (0310; Luigi BL-02, 2026-10-03).
//
// The two AMOUNTS only. Which one applies is a rule in the database and does
// not change here: before pickup, a Scheduled booking cancelled within the
// hour; after pickup, any booking — and the after-pickup fee replaces the
// other, never adds to it. Superadmin only (the page and the table policy).
//
// Who RECEIVES the after-pickup fee is the next card, CancellationShareCard (0313).

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type OperatingSettings, useUpdateOperatingSettings } from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

/** CORE-08 §4.3's approved amounts, so a deliberate change reads as one. */
const APPROVED = { before: 50, after: 100 } as const;

const validFee = (n: number) =>
  Number.isFinite(n) && n >= 0 && n <= 10000 && Math.round(n * 100) === n * 100;

export function CancellationFeeCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [before, setBefore] = useState(() => String(settings.cancellation_fee_before_pickup_php));
  const [after, setAfter] = useState(() => String(settings.cancellation_fee_after_pickup_php));

  useEffect(() => {
    setBefore(String(settings.cancellation_fee_before_pickup_php));
    setAfter(String(settings.cancellation_fee_after_pickup_php));
  }, [settings]);

  const b = Number(before);
  const a = Number(after);
  const valid = validFee(b) && validFee(a);

  function save() {
    if (!valid) {
      toast.error("Each fee has to be an amount from ₱0 to ₱10,000.");
      return;
    }
    update.mutate(
      { cancellation_fee_before_pickup_php: b, cancellation_fee_after_pickup_php: a },
      {
        onSuccess: () =>
          toast.success("Cancellation fees saved. They apply to the next cancellation."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <CardTitle className="type-title-body"><h2>Cancellation fee</h2></CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          What a client is charged for a late cancellation. A change applies from the next
          cancellation; it never re-prices one already made.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label="Before pickup — Scheduled, cancelled within the hour (₱)">
          <NumberInput value={before} onChange={setBefore} />
        </Field>
        <Field label="After a therapist is picked up — any booking (₱)">
          <NumberInput value={after} onChange={setAfter} />
        </Field>
        <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
          The after-pickup fee replaces the before-pickup one. They never add up.
        </p>
        {valid && (b !== APPROVED.before || a !== APPROVED.after) ? (
          <p className="max-w-prose text-sm leading-relaxed text-warning">
            ₱{APPROVED.before} and ₱{APPROVED.after} are the approved amounts. This is a deliberate
            change.
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
